import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { 
  Phone, 
  Plus, 
  Trash2, 
  Settings,
  Check,
  X,
  Mic,
  Globe,
  Wifi,
  WifiOff
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const API = `${process.env.REACT_APP_API_URL}/api`;

export default function PhoneSettings() {
  const { getAuthHeader, business } = useAuth();
  
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [providerStatus, setProviderStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  
  const [newNumber, setNewNumber] = useState({
    number: "",
    agent_id: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [numbersRes, agentsRes, statusRes] = await Promise.all([
        axios.get(`${API}/telephony/numbers/${business?.id}`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API}/agents`, { headers: getAuthHeader() }),
        axios.get(`${API}/telephony/provider/status`, { headers: getAuthHeader() })
      ]);
      setPhoneNumbers(numbersRes.data || []);
      setAgents(agentsRes.data.filter(a => a.is_active));
      setProviderStatus(statusRes.data);
    } catch (error) {
      toast.error("Failed to load phone settings");
    } finally {
      setLoading(false);
    }
  };

  const addPhoneNumber = async () => {
    if (!newNumber.number) {
      toast.error("Please enter a phone number");
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append("number", newNumber.number);
      formData.append("business_id", business?.id);
      if (newNumber.agent_id) {
        formData.append("agent_id", newNumber.agent_id);
      }
      
      await axios.post(`${API}/telephony/numbers`, formData, { headers: getAuthHeader() });
      toast.success("Phone number added!");
      setShowAddDialog(false);
      setNewNumber({ number: "", agent_id: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add phone number");
    }
  };

  const deleteNumber = async (numberId) => {
    if (!confirm("Remove this phone number?")) return;
    try {
      await axios.delete(`${API}/telephony/numbers/${numberId}?business_id=${business?.id}`, { headers: getAuthHeader() });
      toast.success("Phone number removed");
      fetchData();
    } catch (error) {
      toast.error("Failed to remove phone number");
    }
  };

  const getProviderIcon = () => {
    if (!providerStatus) return <WifiOff className="w-5 h-5 text-red-500" />;
    
    if (providerStatus.default_provider === "telnyx") {
      return <Wifi className="w-5 h-5 text-emerald-500" />;
    }
    return <Wifi className="w-5 h-5 text-amber-500" />;
  };

  const getProviderName = () => {
    if (!providerStatus) return "Not Connected";
    if (providerStatus.default_provider === "telnyx") return "Telnyx (SIP)";
    if (providerStatus.default_provider === "mock") return "Demo Mode";
    return providerStatus.default_provider;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="loading-dots flex gap-1">
          <span className="w-2 h-2 bg-primary rounded-full animate-bounce"></span>
          <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: "0.1s" }}></span>
          <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="phone-settings-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight mb-2">Phone Settings</h1>
          <p className="text-muted-foreground">Configure your phone numbers and telephony provider</p>
        </div>
        <Button 
          className="rounded-full bg-gradient-to-r from-cyan-500 to-purple-500"
          onClick={() => setShowAddDialog(true)}
          data-testid="add-number-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Phone Number
        </Button>
      </div>

      {/* Provider Status Card */}
      <Card className="mb-6 bg-gradient-to-br from-slate-900/50 to-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Telephony Provider
          </CardTitle>
          <CardDescription>Your current phone service configuration</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {getProviderIcon()}
              <div>
                <p className="font-semibold">{getProviderName()}</p>
                <p className="text-sm text-muted-foreground">
                  {providerStatus?.default_provider === "mock" 
                    ? "Using simulated calls for testing" 
                    : "Real phone calls enabled"}
                </p>
              </div>
            </div>
            
            {providerStatus?.default_provider === "mock" && (
              <Badge variant="outline" className="text-amber-400 border-amber-400">
                Demo Mode
              </Badge>
            )}
            
            {providerStatus?.default_provider === "telnyx" && (
              <Badge variant="outline" className="text-emerald-400 border-emerald-400">
                Production
              </Badge>
            )}
          </div>
          
          {providerStatus?.default_provider === "mock" && (
            <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <p className="text-sm text-amber-200">
                <strong>Demo Mode Active:</strong> Calls are simulated and not connected to real phone networks. 
                To enable real phone calls, configure your Telnyx credentials in the backend environment.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Phone Numbers List */}
      <Card className="bg-gradient-to-br from-slate-900/50 to-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="w-5 h-5" />
            Phone Numbers
          </CardTitle>
          <CardDescription>
            {phoneNumbers.length > 0 
              ? `You have ${phoneNumbers.length} phone number(s) configured`
              : "Add phone numbers for inbound and outbound calls"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {phoneNumbers.length === 0 ? (
            <div className="text-center py-12">
              <Phone className="w-12 h-12 mx-auto mb-4 text-slate-500" />
              <h3 className="text-lg font-semibold mb-2">No Phone Numbers</h3>
              <p className="text-muted-foreground mb-4">
                Add a phone number to start receiving and making calls
              </p>
              <Button onClick={() => setShowAddDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Number
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {phoneNumbers.map((phone) => (
                <div 
                  key={phone.id}
                  className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <Phone className="w-5 h-5 text-emerald-500" />
                    </div>
                    <div>
                      <p className="font-semibold font-mono">{phone.phone_number}</p>
                      <p className="text-sm text-muted-foreground">
                        {phone.agent_id 
                          ? `Assigned to: ${agents.find(a => a.id === phone.agent_id)?.name || 'Unknown Agent'}`
                          : 'No agent assigned'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {phone.is_active ? (
                      <Badge variant="outline" className="text-emerald-400 border-emerald-400">
                        <Check className="w-3 h-3 mr-1" />
                        Active
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-red-400 border-red-400">
                        <X className="w-3 h-3 mr-1" />
                        Inactive
                      </Badge>
                    )}
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="text-red-400 hover:text-red-300"
                      onClick={() => deleteNumber(phone.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Voice Settings Info */}
      <Card className="mt-6 bg-gradient-to-br from-slate-900/50 to-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mic className="w-5 h-5" />
            Voice Configuration
          </CardTitle>
          <CardDescription>AI voice settings for your agents</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
              <div className="flex items-center gap-2 mb-2">
                <Globe className="w-4 h-4 text-cyan-400" />
                <span className="font-semibold">Languages</span>
              </div>
              <p className="text-sm text-muted-foreground">English, Hindi, Punjabi</p>
            </div>
            <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
              <div className="flex items-center gap-2 mb-2">
                <Mic className="w-4 h-4 text-purple-400" />
                <span className="font-semibold">TTS Provider</span>
              </div>
              <p className="text-sm text-muted-foreground">ElevenLabs + OpenAI</p>
            </div>
            <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
              <div className="flex items-center gap-2 mb-2">
                <Settings className="w-4 h-4 text-amber-400" />
                <span className="font-semibold">Voice Models</span>
              </div>
              <p className="text-sm text-muted-foreground">8 voices available</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Number Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-[425px] bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle>Add Phone Number</DialogTitle>
            <DialogDescription>
              Add a phone number from your Telnyx account or enter a number for testing
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="number">Phone Number</Label>
              <Input
                id="number"
                placeholder="+14165551234"
                value={newNumber.number}
                onChange={(e) => setNewNumber({ ...newNumber, number: e.target.value })}
                className="bg-slate-800 border-slate-700"
              />
              <p className="text-xs text-muted-foreground">
                Enter in E.164 format (e.g., +14165551234)
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="agent">Default Agent (Optional)</Label>
              <Select 
                value={newNumber.agent_id} 
                onValueChange={(v) => setNewNumber({ ...newNumber, agent_id: v })}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue placeholder="Select an agent" />
                </SelectTrigger>
                <SelectContent>
                  {agents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                This agent will handle calls to this number by default
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={addPhoneNumber}>
              Add Number
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
